import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import { getKeycloak, resetKeycloak } from './keycloak';
import {
	closeOAuthWebView,
	hasActiveOAuthWebView,
	markOAuthRedirectReceived,
	openOAuthWebView,
	registerOAuthBrowserListeners,
	resetOAuthRedirectTracking,
} from './keycloakOAuthBrowser';
import { clientDebugLog } from './clientDebugLog';
import { getKeycloakDefaultScope, type KeycloakPortal } from './keycloakOptions';
import { initKeycloak } from './keycloakInit';
import type { InitKeycloakResult } from './keycloakInit';
import {
	getKeycloakRedirectUri,
	hasOAuthCallbackParams,
	isNativeLogoutReturnUrl,
	isNativeOAuthCallbackUrl,
	oauthDeepLinkToHistoryPath,
} from './keycloakRedirect';

let nativeAuthListenerRegistered = false;
let nativeLoginInProgress = false;
let nativeLoginBrowserClosed = false;
let oauthBrowserClosedFallbackRegistered = false;
let nativeAuthResumeGuardRegistered = false;

/** Returns whether a native OAuth browser flow is currently in progress. */
export function isNativeLoginInProgress(): boolean {
	return nativeLoginInProgress;
}

/**
 * Returns true when the browser closed after login but the deep link was never received.
 * UI can use this to show a retry prompt.
 */
export function hasNativeLoginFailed(): boolean {
	return nativeLoginBrowserClosed && !nativeLoginInProgress;
}

/** Resets the browser-closed failure flag (e.g. before retrying login). */
export function clearNativeLoginFailure(): void {
	nativeLoginBrowserClosed = false;
}

/**
 * Completes native OAuth after the app receives a deep-link callback.
 * Closes the in-app browser, rewrites history, and re-initializes Keycloak.
 */
export async function completeNativeOAuthFromDeepLink(deepLinkUrl: string): Promise<InitKeycloakResultCompat> {
	if (!isNativeOAuthCallbackUrl(deepLinkUrl)) {
		return { authenticated: false };
	}

	markOAuthRedirectReceived();
	await closeOAuthWebView();

	const historyPath = oauthDeepLinkToHistoryPath(deepLinkUrl);
	window.history.replaceState({}, '', historyPath);

	if (!hasOAuthCallbackParams(historyPath)) {
		nativeLoginInProgress = false;

		return {
			authenticated: false,
			logoutReturn: isNativeLogoutReturnUrl(deepLinkUrl),
		};
	}

	try {
		if (getKeycloak().didInitialize) {
			resetKeycloak();
		}

		return await initKeycloak();
	} finally {
		nativeLoginInProgress = false;
	}
}

/** Result shape returned by {@link initKeycloak} and native deep-link completion helpers. */
export type InitKeycloakResultCompat = InitKeycloakResult & {
	/** True when Keycloak redirected back after logout (no OAuth code in the URL). */
	logoutReturn?: boolean;
};

/**
 * On cold start, processes a pending native launch URL that contains an OAuth callback.
 * Returns the init result when authentication succeeds, otherwise `null`.
 */
export async function handleKeycloakNativeLaunchUrl(): Promise<InitKeycloakResultCompat | null> {
	if (!Capacitor.isNativePlatform()) {
		return null;
	}

	const launch = await App.getLaunchUrl();

	if (!launch?.url) {
		return null;
	}

	const result = await completeNativeOAuthFromDeepLink(launch.url);

	if (result.authenticated || result.logoutReturn) {
		return result;
	}

	return null;
}

/** Registers a Capacitor `appUrlOpen` listener that completes native OAuth deep links. */
export function registerKeycloakNativeAuthListener(
	onDeepLink: (result: InitKeycloakResultCompat) => Promise<void>
): void {
	if (!Capacitor.isNativePlatform() || nativeAuthListenerRegistered) {
		return;
	}

	nativeAuthListenerRegistered = true;

	void App.addListener('appUrlOpen', (event) => {
		void (async () => {
			clientDebugLog('log', '[OAuthDebug] appUrlOpen', {
				hypothesisId: 'D',
				url: event.url,
				isNativeCallback: isNativeOAuthCallbackUrl(event.url),
			});

			if (!isNativeOAuthCallbackUrl(event.url)) {
				return;
			}

			nativeLoginBrowserClosed = false;
			const result = await completeNativeOAuthFromDeepLink(event.url);

			await onDeepLink(result);
		})();
	});
}

async function handleOAuthBrowserClosedFallback(
	onDeepLink: (result: InitKeycloakResultCompat) => Promise<void>,
	onBrowserClosedWithoutAuth: () => void,
): Promise<void> {
	if (!nativeLoginInProgress) {
		return;
	}

	const launch = await App.getLaunchUrl();
	if (launch?.url && isNativeOAuthCallbackUrl(launch.url)) {
		nativeLoginBrowserClosed = false;
		const result = await completeNativeOAuthFromDeepLink(launch.url);
		await onDeepLink(result);

		return;
	}

	nativeLoginInProgress = false;
	nativeLoginBrowserClosed = true;
	onBrowserClosedWithoutAuth();
}

/**
 * Registers InAppBrowser listeners for WebView redirect interception and
 * browser-closed fallback when OAuth is abandoned without a callback.
 */
export function registerOAuthBrowserClosedFallback(
	onDeepLink: (result: InitKeycloakResultCompat) => Promise<void>,
	onBrowserClosedWithoutAuth: () => void,
): void {
	if (!Capacitor.isNativePlatform() || oauthBrowserClosedFallbackRegistered) {
		return;
	}

	oauthBrowserClosedFallbackRegistered = true;

	registerOAuthBrowserListeners({
		isLoginInProgress: () => nativeLoginInProgress,
		onOAuthRedirect: async (url) => {
			nativeLoginBrowserClosed = false;
			const result = await completeNativeOAuthFromDeepLink(url);
			await onDeepLink(result);
		},
		onBrowserClosedWithoutAuth: () => {
			void handleOAuthBrowserClosedFallback(onDeepLink, onBrowserClosedWithoutAuth);
		},
	});
}

/**
 * @deprecated Use {@link registerOAuthBrowserClosedFallback}.
 */
export function registerBrowserFinishedFallback(
	onDeepLink: (result: InitKeycloakResultCompat) => Promise<void>,
	onBrowserClosedWithoutAuth: () => void,
): void {
	registerOAuthBrowserClosedFallback(onDeepLink, onBrowserClosedWithoutAuth);
}

/**
 * Closes any stale OAuth WebView when the app resumes and login is not active.
 * Only closes when a WebView is actually open to avoid Capgo close() pause/resume loops.
 */
export function registerNativeAuthResumeGuard(): void {
	if (!Capacitor.isNativePlatform() || nativeAuthResumeGuardRegistered) {
		return;
	}

	nativeAuthResumeGuardRegistered = true;

	void App.addListener('appStateChange', ({ isActive }) => {
		if (isActive && !nativeLoginInProgress && hasActiveOAuthWebView()) {
			void closeOAuthWebView();
		}
	});
}

/** Opens the OAuth WebView for Keycloak login on native platforms. */
export async function loginWithKeycloakNative(
	redirectUri: string = getKeycloakRedirectUri(),
	portal?: KeycloakPortal
): Promise<void> {
	if (nativeLoginInProgress) {
		return;
	}

	nativeLoginInProgress = true;
	nativeLoginBrowserClosed = false;
	resetOAuthRedirectTracking();

	try {
		const loginOptions: Record<string, string> = {
			redirectUri,
			scope: getKeycloakDefaultScope(),
		};

		if (portal) {
			loginOptions.portal = portal;
		}

		const loginUrl = await getKeycloak().createLoginUrl(loginOptions);

		clientDebugLog('log', '[OAuthDebug] opening Capgo OAuth WebView', {
			hypothesisId: 'E',
			mode: 'openWebView',
			redirectUri,
			loginUrlHost: (() => {
				try {
					return new URL(loginUrl).host;
				} catch {
					return null;
				}
			})(),
		});

		await openOAuthWebView(loginUrl);
	} catch (error) {
		nativeLoginInProgress = false;
		throw error;
	}
}

/** Opens the OAuth WebView for Keycloak logout on native platforms. */
export async function logoutWithKeycloakNative(redirectUri: string = getKeycloakRedirectUri()): Promise<void> {
	const { buildKeycloakEndSessionUrl } = await import('./keycloakEndSession');

	await openOAuthWebView(buildKeycloakEndSessionUrl(redirectUri));
}

/** Resets native auth module state for tests. */
export function resetKeycloakNativeAuthForTests(): void {
	nativeAuthListenerRegistered = false;
	nativeLoginInProgress = false;
	nativeLoginBrowserClosed = false;
	oauthBrowserClosedFallbackRegistered = false;
	nativeAuthResumeGuardRegistered = false;
}
